import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  IconButton,
  Typography,
  Card,
  CardContent,
  Fade,
  InputAdornment,
  Container,
  Divider,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Tag as TagIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';

interface InnerItem {
  id: string;
  report: string;
  hash: string;
}

interface Section {
  id: string;
  number: string;
  items: InnerItem[];
}

const DynamicNestedForm = ({
  formStateEdit,
  handleFormEditChange,
}: {
  formStateEdit: any;
  handleFormEditChange: any;
}) => {
  const reverseTransform = (objectData: any) => {
    const sections = new Map(); // Using Map to group by section number

    // Process each key-value pair
    Object.entries(objectData).forEach(([key, hash]) => {
      // Extract section number and report name
      const match = key.match(/^ds(\d+)\.(.+)$/);
      if (!match) return;

      const [, sectionNumber, report] = match;

      // Get or create section
      if (!sections.has(sectionNumber)) {
        sections.set(sectionNumber, {
          id: `section-${Date.now()}-${sectionNumber}`,
          number: sectionNumber,
          items: [],
        });
      }

      // Add item to section
      const section = sections.get(sectionNumber);
      section.items.push({
        id: `item-${Date.now()}-${report}`,
        report,
        hash,
      });
    });

    // Convert Map to array and sort by section number
    return Array.from(sections.values()).sort(
      (a, b) => parseInt(a.number) - parseInt(b.number)
    );
  };

  console.log(Object.entries(formStateEdit.params), 'formStateEdit.params');

  const [sections, setSections] = useState<Section[]>(
    formStateEdit.params ? reverseTransform(formStateEdit.params) : []
  );

  const addSection = () => {
    const newSection: Section = {
      id: `section-${Date.now()}`,
      number: '',
      items: [],
    };
    setSections([...sections, newSection]);
  };

  const addInnerItem = (sectionId: string) => {
    setSections(
      sections.map((section) => {
        if (section.id === sectionId) {
          return {
            ...section,
            items: [
              ...section.items,
              {
                id: `item-${Date.now()}`,
                report: '',
                hash: '',
              },
            ],
          };
        }
        return section;
      })
    );
  };

  const updateSectionNumber = (sectionId: string, value: string) => {
    setSections(
      sections.map((section) =>
        section.id === sectionId ? { ...section, number: value } : section
      )
    );
  };

  const updateInnerItem = (
    sectionId: string,
    itemId: string,
    field: keyof InnerItem,
    value: string
  ) => {
    setSections(
      sections.map((section) => {
        if (section.id === sectionId) {
          return {
            ...section,
            items: section.items.map((item) =>
              item.id === itemId ? { ...item, [field]: value } : item
            ),
          };
        }
        return section;
      })
    );
  };

  const removeSection = (sectionId: string) => {
    setSections(sections.filter((section) => section.id !== sectionId));
  };

  const removeInnerItem = (sectionId: string, itemId: string) => {
    setSections(
      sections.map((section) => {
        if (section.id === sectionId) {
          return {
            ...section,
            items: section.items.filter((item) => item.id !== itemId),
          };
        }
        return section;
      })
    );
  };

  const hashOptions = [
    { value: 'identity', label: 'identity' },
    { value: 'report', label: 'report' },
    {
      value: 'brd_param',
      label: 'brd_param',
    },
    { value: 'end_date', label: 'end_date' },
    { value: 'start_date', label: 'start_date' },

    { value: 'ecpc_weight', label: 'ecpc_weight' },
    { value: 'rev_weight', label: 'rev_weight' },
    { value: 'aov_weight', label: 'aov_weight' },
    { value: 'click_weight', label: 'click_weight' },
    { value: 'cr_weight', label: 'cr_weight' },
    { value: 'newvexist_weight', label: 'newvexist_weight' },
    { value: 'conversions_weight', label: 'conversions_weight' },
    {
      value: 'new_user_percentage_weight',
      label: 'new_user_percentage_weight',
    },
  ];

  useEffect(() => {
    handleFormEditChange('params', sections);
  }, [sections]);

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50' }}>
          <Typography variant="h5" gutterBottom align="center" sx={{ mb: 4 }}>
            Dynamic Form Builder
          </Typography>

          {/* Sections */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {sections.map((section) => (
              <Fade key={section.id} in={true}>
                <Card variant="outlined">
                  <CardContent>
                    {/* Section Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <TextField
                        label="DS Number"
                        value={section.number}
                        onChange={(e) =>
                          updateSectionNumber(section.id, e.target.value)
                        }
                        variant="outlined"
                        size="small"
                        sx={{ width: 120 }}
                      />
                      <IconButton
                        onClick={() => removeSection(section.id)}
                        color="error"
                        sx={{ ml: 'auto' }}>
                        <CloseIcon />
                      </IconButton>
                    </Box>

                    {/* Inner Items */}
                    <Box
                      sx={{
                        pl: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                      }}>
                      {section.items.map((item) => (
                        <Card
                          key={item.id}
                          variant="outlined"
                          sx={{
                            bgcolor: 'grey.50',
                            '&:hover': {
                              bgcolor: 'grey.100',
                              transition: 'all 0.3s',
                            },
                          }}>
                          <CardContent>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                              }}>
                              <TextField
                                fullWidth
                                label="Param"
                                value={item.report}
                                onChange={(e) =>
                                  updateInnerItem(
                                    section.id,
                                    item.id,
                                    'report',
                                    e.target.value
                                  )
                                }
                                variant="outlined"
                                size="small"
                              />
                              {/* <TextField
                                fullWidth
                                label="Mapping"
                                value={item.hash}
                                onChange={(e) =>
                                  updateInnerItem(
                                    section.id,
                                    item.id,
                                    'hash',
                                    e.target.value
                                  )
                                }
                                variant="outlined"
                                size="small"
                              /> */}
                              <FormControl fullWidth size="small">
                                <InputLabel>Params Type</InputLabel>
                                <Select
                                  disabled={formStateEdit.reportType === 'Existing'}
                                  value={item.hash}
                                  onChange={(e) =>
                                    updateInnerItem(
                                      section.id,
                                      item.id,
                                      'hash',
                                      e.target.value
                                    )
                                  }
                                  label="Params Type">
                                  {hashOptions.map((option) => (
                                    <MenuItem
                                      key={option.value}
                                      value={option.value}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                }}>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    removeInnerItem(section.id, item.id)
                                  }
                                  color="error">
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}

                      {/* Add Inner Item Button */}
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => addInnerItem(section.id)}
                        sx={{ alignSelf: 'flex-start' }}>
                        Add Input Fields
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Fade>
            ))}
          </Box>

          {/* Add Section Button */}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addSection}
            fullWidth
            sx={{ mt: 3 }}>
            Add New Section
          </Button>
        </Paper>
      </Box>
    </Container>
  );
};

export default DynamicNestedForm;
